/** Asset types (with meter templates and allowed parents) and make/models. */
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  ASSET_CATEGORIES,
  type AssetType,
  type AssetTypeIn,
  type AssetTypePatch,
  type ItemCategory,
  type MeterType,
  type Model,
  type ModelIn,
  type ModelPatch,
  type ReorderPolicy,
  useAllReorderPolicies,
  useAssetTypesAdmin,
  useCreateMeterType,
  useDeleteItemCategory,
  useHierarchyCompatibility,
  useItemCategories,
  useMeterTypes,
  useModels,
  useSaveAssetType,
  useSaveItemCategory,
  useSaveModel,
  useSetReorderPolicy,
} from "@/features/admin/reference/queries";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { useListState } from "@/shared/lib/list-state";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import {
  AssetTypePicker,
  ItemPicker,
  LocationPicker,
  PartyPicker,
} from "@/shared/components/pickers";
import { formatQuantity } from "@/shared/lib/format";
import { nullable, numberOrNull, useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/primitives";

interface TypeValues extends Record<string, unknown> {
  key: string;
  name: string;
  name_ar: string;
  category: string;
  icon: string;
  is_vessel: boolean;
  is_component: boolean;
  is_serialised: boolean;
  is_active: boolean;
}

interface MeterTemplateRow {
  meter_type_key: string;
  name: string;
  is_primary: boolean;
}

/** Asset type create/edit dialog. */
export function AssetTypeDialog({
  type,
  allTypes,
  onClose,
}: {
  type?: AssetType;
  allTypes: AssetType[];
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveAssetType();
  const meterTypes = useMeterTypes();
  const [parents, setParents] = useState<string[]>(type?.allowed_parent_type_ids ?? []);
  const [templates, setTemplates] = useState<MeterTemplateRow[]>(
    type?.meter_templates.map((m) => ({
      meter_type_key: m.meter_type_key,
      name: m.name,
      is_primary: m.is_primary,
    })) ?? [],
  );
  const initial = useMemo<TypeValues>(
    () => ({
      key: type?.key ?? "",
      name: type?.name ?? "",
      name_ar: type?.name_i18n?.ar ?? "",
      category: type?.category ?? "other",
      icon: type?.icon ?? "",
      is_vessel: type?.is_vessel ?? false,
      is_component: type?.is_component ?? false,
      is_serialised: type?.is_serialised ?? true,
      is_active: type?.is_active ?? true,
    }),
    [type],
  );
  const form = useForm<TypeValues>({
    initial,
    schema: z.object({
      key: type ? z.string() : z.string().regex(/^[a-z0-9_-]{2,50}$/, t("roles.keyFormat")),
      name: z.string().trim().min(1, t("app.required")),
    }),
    idPrefix: "atype",
  });
  const updateTemplate = (i: number, patch: Partial<MeterTemplateRow>): void =>
    setTemplates(templates.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const name_i18n = { en: v.name.trim(), ...(v.name_ar ? { ar: v.name_ar } : {}) };
      const common = {
        name: v.name.trim(),
        name_i18n,
        category: v.category,
        icon: v.icon,
        is_vessel: v.is_vessel,
        is_component: v.is_component,
        is_serialised: v.is_serialised,
        allowed_parent_type_ids: parents,
      };
      const body: AssetTypeIn | AssetTypePatch = type
        ? {
            ...common,
            is_active: v.is_active,
            meter_templates: templates.filter((m) => m.meter_type_key && m.name.trim()),
          }
        : { ...common, key: v.key };
      const saved = await save.mutateAsync({ id: type?.id, body });
      // Meter templates are only settable via PATCH; apply them after creation.
      if (!type && templates.length) {
        await save.mutateAsync({
          id: saved.id,
          body: { meter_templates: templates.filter((m) => m.meter_type_key && m.name.trim()) },
        });
      }
    });
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={type ? t("assetTypes.edit") : t("assetTypes.new")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      wide
    >
      {type?.is_system ? (
        <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
          {t("assetTypes.systemNote")}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id="atype-key"
          label={t("app.key")}
          error={form.errors.key}
          hint={t("roles.keyHint")}
        >
          <Input {...form.field("key")} disabled={!!type} />
        </Field>
        <Field id="atype-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} />
        </Field>
        <Field id="atype-name_ar" label={t("assetTypes.nameAr")}>
          <Input dir="rtl" {...form.field("name_ar")} />
        </Field>
        <Field id="atype-category" label={t("assetTypes.category")}>
          <Select
            id="atype-category"
            value={form.values.category}
            onChange={(e) => form.set("category", e.target.value)}
          >
            {ASSET_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`assetTypes.categories.${c}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="atype-icon" label={t("assetTypes.icon")}>
          <Input {...form.field("icon")} />
        </Field>
      </div>
      <div className="flex flex-wrap gap-4">
        <Checkbox
          id="atype-is_vessel"
          label={t("assetTypes.isVessel")}
          checked={form.values.is_vessel}
          onChange={(e) => form.set("is_vessel", e.target.checked)}
        />
        <Checkbox
          id="atype-is_component"
          label={t("assetTypes.isComponent")}
          checked={form.values.is_component}
          onChange={(e) => form.set("is_component", e.target.checked)}
        />
        <Checkbox
          id="atype-is_serialised"
          label={t("assetTypes.isSerialised")}
          checked={form.values.is_serialised}
          onChange={(e) => form.set("is_serialised", e.target.checked)}
        />
        {type ? (
          <Checkbox
            id="atype-is_active"
            label={t("app.active")}
            checked={form.values.is_active}
            onChange={(e) => form.set("is_active", e.target.checked)}
          />
        ) : null}
      </div>
      <Field
        id="atype-parents"
        label={t("assetTypes.allowedParents")}
        hint={t("assetTypes.allowedParentsHint")}
      >
        <div className="flex flex-wrap gap-3" role="group">
          {allTypes
            .filter((x) => x.id !== type?.id)
            .map((x) => (
              <Checkbox
                key={x.id}
                id={`atype-parent-${x.key}`}
                label={x.name}
                checked={parents.includes(x.id)}
                onChange={(e) =>
                  setParents(
                    e.target.checked ? [...parents, x.id] : parents.filter((p) => p !== x.id),
                  )
                }
              />
            ))}
        </div>
      </Field>
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{t("assetTypes.meterTemplates")}</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setTemplates([
                ...templates,
                { meter_type_key: "", name: "", is_primary: templates.length === 0 },
              ])
            }
          >
            {t("assetTypes.addMeter")}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t("assetTypes.meterTemplatesHint")}</p>
        {templates.map((m, i) => (
          <div
            key={i}
            className="grid grid-cols-1 gap-2 border-t pt-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]"
          >
            <Select
              aria-label={`${t("assetTypes.meterType")} ${i + 1}`}
              value={m.meter_type_key}
              onChange={(e) =>
                updateTemplate(i, {
                  meter_type_key: e.target.value,
                  name:
                    m.name || (meterTypes.data?.find((x) => x.key === e.target.value)?.name ?? ""),
                })
              }
            >
              <option value="">—</option>
              {(meterTypes.data ?? []).map((mt) => (
                <option key={mt.key} value={mt.key}>
                  {mt.name} ({mt.unit})
                </option>
              ))}
            </Select>
            <Input
              aria-label={`${t("app.name")} ${i + 1}`}
              value={m.name}
              onChange={(e) => updateTemplate(i, { name: e.target.value })}
            />
            <Checkbox
              id={`atype-meter-primary-${i}`}
              label={t("assetTypes.primary")}
              checked={m.is_primary}
              onChange={(e) =>
                setTemplates(
                  templates.map((x, idx) => ({
                    ...x,
                    is_primary:
                      idx === i ? e.target.checked : e.target.checked ? false : x.is_primary,
                  })),
                )
              }
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label={`${t("app.delete")} ${i + 1}`}
              onClick={() => setTemplates(templates.filter((_, idx) => idx !== i))}
            >
              ×
            </Button>
          </div>
        ))}
      </div>
    </FormDialog>
  );
}

interface ModelValues extends Record<string, unknown> {
  name: string;
  manufacturer_id: string;
  asset_type_id: string;
  notes: string;
  is_active: boolean;
}

/** Make/model dialog. */
export function ModelDialog({
  model,
  onClose,
}: {
  model?: Model;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveModel();
  const initial = useMemo<ModelValues>(
    () => ({
      name: model?.name ?? "",
      manufacturer_id: model?.manufacturer_id ?? "",
      asset_type_id: model?.asset_type_id ?? "",
      notes: model?.notes ?? "",
      is_active: model?.is_active ?? true,
    }),
    [model],
  );
  const form = useForm<ModelValues>({
    initial,
    schema: z.object({ name: z.string().trim().min(1, t("app.required")) }),
    idPrefix: "model",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const body: ModelIn | ModelPatch = {
        name: v.name.trim(),
        manufacturer_id: nullable(v.manufacturer_id),
        asset_type_id: nullable(v.asset_type_id),
        notes: v.notes,
        ...(model ? { is_active: v.is_active } : { specs: {} }),
      };
      await save.mutateAsync({ id: model?.id, body });
    });
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={model ? t("assetTypes.editModel") : t("assetTypes.newModel")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <Field id="model-name" label={t("app.name")} error={form.errors.name}>
        <Input {...form.field("name")} />
      </Field>
      <Field id="model-manufacturer_id" label={t("assets.manufacturer")}>
        <PartyPicker
          id="model-manufacturer_id"
          role="manufacturer"
          value={form.values.manufacturer_id}
          onChange={(id) => form.set("manufacturer_id", id ?? "")}
        />
      </Field>
      <Field id="model-asset_type_id" label={t("app.type")}>
        <AssetTypePicker
          id="model-asset_type_id"
          value={form.values.asset_type_id}
          onChange={(id) => form.set("asset_type_id", id ?? "")}
        />
      </Field>
      <Field id="model-notes" label={t("app.notes")}>
        <Textarea {...form.field("notes")} />
      </Field>
      {model ? (
        <Checkbox
          id="model-is_active"
          label={t("app.active")}
          checked={form.values.is_active}
          onChange={(e) => form.set("is_active", e.target.checked)}
        />
      ) : null}
    </FormDialog>
  );
}

interface MeterTypeValues extends Record<string, unknown> {
  key: string;
  name: string;
  name_ar: string;
  unit: string;
  kind: string;
  decimals: string;
}

/** Per-tenant meter type creation dialog. */
export function MeterTypeDialog({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation();
  const create = useCreateMeterType();
  const form = useForm<MeterTypeValues>({
    initial: {
      key: "",
      name: "",
      name_ar: "",
      unit: "h",
      kind: "counter",
      decimals: "1",
    },
    schema: z.object({
      key: z.string().regex(/^[a-z0-9_-]{2,40}$/, t("roles.keyFormat")),
      name: z.string().trim().min(1, t("app.required")),
      unit: z.string().trim().min(1, t("app.required")),
    }),
    idPrefix: "mtype",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      create.mutateAsync({
        key: v.key,
        name: v.name.trim(),
        name_i18n: { en: v.name.trim(), ...(v.name_ar ? { ar: v.name_ar } : {}) },
        unit: v.unit.trim(),
        kind: v.kind,
        decimals: Number(v.decimals) || 0,
      }),
    );
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t("assetTypes.newMeterType")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id="mtype-key"
          label={t("app.key")}
          error={form.errors.key}
          hint={t("roles.keyHint")}
        >
          <Input {...form.field("key")} />
        </Field>
        <Field id="mtype-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} />
        </Field>
        <Field id="mtype-name_ar" label={t("assetTypes.nameAr")}>
          <Input dir="rtl" {...form.field("name_ar")} />
        </Field>
        <Field id="mtype-unit" label={t("app.unit")} error={form.errors.unit}>
          <Input {...form.field("unit")} />
        </Field>
        <Field id="mtype-kind" label={t("app.type")}>
          <Select
            id="mtype-kind"
            value={form.values.kind}
            onChange={(e) => form.set("kind", e.target.value)}
          >
            <option value="counter">{t("assetTypes.counter")}</option>
            <option value="gauge">{t("assetTypes.gauge")}</option>
          </Select>
        </Field>
        <Field id="mtype-decimals" label={t("assetTypes.decimals")}>
          <Input type="number" min="0" max="4" {...form.field("decimals")} />
        </Field>
      </div>
    </FormDialog>
  );
}

/** Per-tenant item category create/edit dialog. */
function ItemCategoryDialog({
  category,
  categories,
  onClose,
}: {
  category?: ItemCategory;
  categories: ItemCategory[];
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveItemCategory();
  const form = useForm({
    initial: {
      key: category?.key ?? "",
      name: category?.name ?? "",
      name_ar: category?.name_i18n?.ar ?? "",
      parent_id: category?.parent_id ?? "",
    },
    schema: z.object({
      key: category ? z.string() : z.string().regex(/^[a-z0-9_-]{2,40}$/, t("roles.keyFormat")),
      name: z.string().trim().min(1, t("app.required")),
    }),
    idPrefix: "icat",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      save.mutateAsync({
        id: category?.id,
        body: {
          ...(category ? {} : { key: v.key }),
          name: v.name.trim(),
          name_i18n: { en: v.name.trim(), ...(v.name_ar ? { ar: v.name_ar } : {}) },
          parent_id: nullable(v.parent_id),
        },
      }),
    );
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={category ? t("inventory.editCategory") : t("inventory.newCategory")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <Field id="icat-key" label={t("app.key")} error={form.errors.key} hint={t("roles.keyHint")}>
        <Input {...form.field("key")} disabled={!!category} />
      </Field>
      <Field id="icat-name" label={t("app.name")} error={form.errors.name}>
        <Input {...form.field("name")} />
      </Field>
      <Field id="icat-name_ar" label={t("assetTypes.nameAr")}>
        <Input dir="rtl" {...form.field("name_ar")} />
      </Field>
      <Field id="icat-parent_id" label={t("inventory.parentCategory")}>
        <Select
          id="icat-parent_id"
          value={form.values.parent_id}
          onChange={(e) => form.set("parent_id", e.target.value)}
        >
          <option value="">—</option>
          {categories
            .filter((c) => c.id !== category?.id)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
        </Select>
      </Field>
    </FormDialog>
  );
}

/** Per-tenant reorder policy create/edit dialog. */
function AdminReorderPolicyDialog({
  policy,
  onClose,
}: {
  policy?: ReorderPolicy;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSetReorderPolicy();
  const form = useForm({
    initial: {
      item_id: policy?.item_id ?? "",
      location_id: policy?.location_id ?? "",
      min_qty: policy?.min_qty ?? "0",
      max_qty: policy?.max_qty ?? "",
      reorder_qty: policy?.reorder_qty ?? "",
      lead_time_days:
        policy?.lead_time_days === null || policy?.lead_time_days === undefined
          ? ""
          : String(policy.lead_time_days),
      is_active: policy?.is_active ?? true,
    },
    schema: z.object({
      item_id: z.string().min(1, t("app.required")),
      location_id: z.string().min(1, t("app.required")),
      min_qty: z.string().min(1, t("app.required")),
    }),
    idPrefix: "rpol",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      save.mutateAsync({
        item_id: v.item_id,
        location_id: v.location_id,
        min_qty: Number(v.min_qty),
        max_qty: numberOrNull(v.max_qty),
        reorder_qty: numberOrNull(v.reorder_qty),
        lead_time_days: numberOrNull(v.lead_time_days),
        is_active: v.is_active,
      }),
    );
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t("inventory.reorderPolicy")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <Field id="rpol-item_id" label={t("inventory.title")} error={form.errors.item_id}>
        {policy ? (
          <Input id="rpol-item_id" value={`${policy.item_sku} · ${policy.item_name}`} disabled />
        ) : (
          <ItemPicker
            id="rpol-item_id"
            value={form.values.item_id}
            onChange={(id) => form.set("item_id", id ?? "")}
          />
        )}
      </Field>
      <Field id="rpol-location_id" label={t("inventory.location")} error={form.errors.location_id}>
        <LocationPicker
          id="rpol-location_id"
          value={form.values.location_id}
          onChange={(id) => form.set("location_id", id ?? "")}
          stockOnly
          disabled={!!policy}
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="rpol-min_qty" label={t("inventory.minQty")} error={form.errors.min_qty}>
          <Input type="number" min="0" step="any" {...form.field("min_qty")} />
        </Field>
        <Field id="rpol-max_qty" label={t("inventory.maxQty")}>
          <Input type="number" min="0" step="any" {...form.field("max_qty")} />
        </Field>
        <Field
          id="rpol-reorder_qty"
          label={t("inventory.reorderQty")}
          hint={t("inventory.reorderQtyHint")}
        >
          <Input type="number" min="0" step="any" {...form.field("reorder_qty")} />
        </Field>
        <Field id="rpol-lead_time_days" label={t("inventory.leadTime")}>
          <Input type="number" min="0" {...form.field("lead_time_days")} />
        </Field>
      </div>
      <Checkbox
        id="rpol-is_active"
        label={t("app.active")}
        checked={form.values.is_active}
        onChange={(e) => form.set("is_active", e.target.checked)}
      />
    </FormDialog>
  );
}

/** Asset types, models, meter types, item categories & reorder policies page. */
export function AssetTypesPage(): ReactElement {
  const { t } = useTranslation();
  const list = useListState(".");
  const { can } = useAuth();
  const manage = can("assets.assettype.manage");
  const manageMeters = can("meters.meter.manage");
  const manageItems = can("inventory.item.manage");
  const viewReorder = can("inventory.reorder.view");
  const manageReorder = can("inventory.reorder.manage");
  const activeTab =
    list.search.tab === "models"
      ? "models"
      : list.search.tab === "meters"
        ? "meters"
        : list.search.tab === "categories"
          ? "categories"
          : list.search.tab === "reorder" && viewReorder
            ? "reorder"
            : "types";
  const types = useAssetTypesAdmin();
  const models = useModels();
  const meterTypes = useMeterTypes();
  const compatibility = useHierarchyCompatibility();
  const categories = useItemCategories();
  const deleteCat = useDeleteItemCategory();
  const policies = useAllReorderPolicies(viewReorder && activeTab === "reorder");
  const [dialog, setDialog] = useState<
    | { kind: "type"; type?: AssetType }
    | { kind: "model"; model?: Model }
    | { kind: "meter" }
    | { kind: "category"; category?: ItemCategory }
    | { kind: "policy"; policy?: ReorderPolicy }
    | null
  >(null);
  const typeName = (id: string | null | undefined): string =>
    types.data?.find((x) => x.id === id)?.name ?? "—";
  const catName = (id: string | null | undefined): string =>
    categories.data?.find((x) => x.id === id)?.name ?? "—";

  const typeColumns: ColumnDef<AssetType>[] = [
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => (
        <div>
          <div className={c.row.original.is_active ? "font-medium" : "font-medium line-through"}>
            {c.row.original.name}{" "}
            {c.row.original.is_system ? <Badge tone="outline">{t("roles.system")}</Badge> : null}
          </div>
          <div className="font-mono text-xs text-muted-foreground">{c.row.original.key}</div>
        </div>
      ),
    },
    {
      id: "category",
      header: t("assetTypes.category"),
      cell: (c) => t(`assetTypes.categories.${c.row.original.category}`),
    },
    {
      id: "flags",
      header: t("app.type"),
      cell: (c) => (
        <span className="flex gap-1">
          {c.row.original.is_vessel ? <Badge tone="info">{t("assetTypes.vessel")}</Badge> : null}
          {c.row.original.is_component ? <Badge>{t("assetTypes.component")}</Badge> : null}
        </span>
      ),
    },
    {
      id: "meters",
      header: t("assets.meters"),
      cell: (c) => c.row.original.meter_templates.map((m) => m.name).join(", ") || "—",
    },
    {
      id: "attrs",
      header: t("assets.attributes"),
      cell: (c) => c.row.original.attribute_schema.length,
    },
    { id: "count", header: t("nav.assets"), cell: (c) => c.row.original.asset_count ?? 0 },
    ...(manage
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDialog({ kind: "type", type: c.row.original })}
              >
                {t("app.edit")}
              </Button>
            ),
          } satisfies ColumnDef<AssetType>,
        ]
      : []),
  ];
  const modelColumns: ColumnDef<Model>[] = [
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => (
        <span className={c.row.original.is_active ? "font-medium" : "font-medium line-through"}>
          {c.row.original.name}
        </span>
      ),
    },
    {
      id: "manufacturer",
      header: t("assets.manufacturer"),
      cell: (c) => c.row.original.manufacturer_name ?? "—",
    },
    { id: "type", header: t("app.type"), cell: (c) => typeName(c.row.original.asset_type_id) },
    ...(manage
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDialog({ kind: "model", model: c.row.original })}
              >
                {t("app.edit")}
              </Button>
            ),
          } satisfies ColumnDef<Model>,
        ]
      : []),
  ];

  const meterColumns: ColumnDef<MeterType>[] = [
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => (
        <div>
          <div className="font-medium">
            {c.row.original.name}{" "}
            {c.row.original.is_system ? <Badge tone="outline">{t("roles.system")}</Badge> : null}
          </div>
          <div className="font-mono text-xs text-muted-foreground">{c.row.original.key}</div>
        </div>
      ),
    },
    {
      id: "kind",
      header: t("app.type"),
      cell: (c) => (
        <Badge tone="outline">
          {c.row.original.kind === "gauge" ? t("assetTypes.gauge") : t("assetTypes.counter")}
        </Badge>
      ),
    },
    { id: "unit", header: t("app.unit"), cell: (c) => c.row.original.unit },
    { id: "decimals", header: t("assetTypes.decimals"), cell: (c) => c.row.original.decimals },
  ];

  const categoryColumns: ColumnDef<ItemCategory>[] = [
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => (
        <div>
          <div className="font-medium">{c.row.original.name}</div>
          <div className="font-mono text-xs text-muted-foreground">{c.row.original.key}</div>
        </div>
      ),
    },
    {
      id: "parent",
      header: t("inventory.parentCategory"),
      cell: (c) => catName(c.row.original.parent_id),
    },
    ...(manageItems
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog({ kind: "category", category: c.row.original })}
                >
                  {t("app.edit")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => {
                    if (globalThis.confirm(t("app.confirmDelete"))) {
                      deleteCat.mutate(c.row.original.id);
                    }
                  }}
                >
                  {t("app.delete")}
                </Button>
              </div>
            ),
          } satisfies ColumnDef<ItemCategory>,
        ]
      : []),
  ];

  const policyColumns: ColumnDef<ReorderPolicy>[] = [
    {
      id: "item",
      header: t("inventory.title"),
      cell: (c) => (
        <div>
          <div className="font-medium">{c.row.original.item_name || "—"}</div>
          <div className="font-mono text-xs text-muted-foreground">
            {c.row.original.item_sku || c.row.original.item_id}
          </div>
        </div>
      ),
    },
    {
      id: "location",
      header: t("inventory.location"),
      cell: (c) => c.row.original.location_code || c.row.original.location_path || "—",
    },
    {
      id: "min",
      header: t("inventory.minQty"),
      cell: (c) => <span className="tabular-nums">{formatQuantity(c.row.original.min_qty)}</span>,
    },
    {
      id: "max",
      header: t("inventory.maxQty"),
      cell: (c) => <span className="tabular-nums">{formatQuantity(c.row.original.max_qty)}</span>,
    },
    {
      id: "lead",
      header: t("inventory.leadTime"),
      cell: (c) => c.row.original.lead_time_days ?? "—",
    },
    ...(manageReorder
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDialog({ kind: "policy", policy: c.row.original })}
              >
                {t("app.edit")}
              </Button>
            ),
          } satisfies ColumnDef<ReorderPolicy>,
        ]
      : []),
  ];

  const hasCompatibilityWarnings =
    (compatibility.data?.unconfigured_component_type_keys.length ?? 0) > 0 ||
    (compatibility.data?.incompatible_relationships.length ?? 0) > 0;

  return (
    <div>
      <PageHeader title={t("assetTypes.title")} subtitle={t("assetTypes.subtitle")} />
      {hasCompatibilityWarnings ? (
        <div
          role="region"
          aria-label={t("assetTypes.compatibilityWarning")}
          className="mb-3 space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950"
        >
          <div className="font-semibold">{t("assetTypes.compatibilityWarning")}</div>
          {compatibility.data?.unconfigured_component_type_keys.length ? (
            <p>
              {t("assetTypes.unconfiguredComponentTypes", {
                types: compatibility.data.unconfigured_component_type_keys.join(", "),
              })}
            </p>
          ) : null}
          {compatibility.data?.incompatible_relationships.length ? (
            <p>
              {t("assetTypes.incompatibleRelationshipsCount", {
                count: compatibility.data.incompatible_relationships.length,
              })}
            </p>
          ) : null}
        </div>
      ) : null}
      {dialog?.kind === "type" ? (
        <AssetTypeDialog
          type={dialog.type}
          allTypes={types.data ?? []}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "model" ? (
        <ModelDialog model={dialog.model} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "meter" ? <MeterTypeDialog onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "category" ? (
        <ItemCategoryDialog
          category={dialog.category}
          categories={categories.data ?? []}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "policy" ? (
        <AdminReorderPolicyDialog policy={dialog.policy} onClose={() => setDialog(null)} />
      ) : null}
      <Tabs
        value={activeTab}
        onValueChange={(tab) => list.update({ tab, q: undefined, sort: undefined })}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="types">{t("assetTypes.title")}</TabsTrigger>
            <TabsTrigger value="models">{t("assetTypes.models")}</TabsTrigger>
            <TabsTrigger value="meters">{t("assetTypes.meterTypes")}</TabsTrigger>
            <TabsTrigger value="categories">{t("inventory.categories")}</TabsTrigger>
            {viewReorder ? (
              <TabsTrigger value="reorder">{t("inventory.reorderPolicies")}</TabsTrigger>
            ) : null}
          </TabsList>
          <div className="flex flex-wrap gap-2">
            {manage ? (
              <>
                <Button size="sm" onClick={() => setDialog({ kind: "type" })}>
                  {t("assetTypes.new")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "model" })}>
                  {t("assetTypes.newModel")}
                </Button>
              </>
            ) : null}
            {manageMeters && activeTab === "meters" ? (
              <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "meter" })}>
                {t("assetTypes.newMeterType")}
              </Button>
            ) : null}
            {manageItems && activeTab === "categories" ? (
              <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "category" })}>
                {t("inventory.newCategory")}
              </Button>
            ) : null}
            {manageReorder && activeTab === "reorder" ? (
              <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "policy" })}>
                {t("inventory.addPolicy")}
              </Button>
            ) : null}
          </div>
        </div>
        <TabsContent value="types">
          {types.isPending ? (
            <LoadingState />
          ) : types.isError ? (
            <ErrorState error={types.error} onRetry={() => void types.refetch()} />
          ) : (
            <CompleteDataTable
              columns={typeColumns}
              data={types.data}
              getSearchText={(type) => `${type.key} ${type.name}`}
              searchDescription={t("tablePilot.searchFields", {
                fields: `${t("app.key")}, ${t("app.name")}`,
              })}
              sortFields={[{ value: "name", label: t("app.name"), valueOf: (type) => type.name }]}
              minWidth={850}
              getRowId={(x) => x.id}
              total={types.data.length}
            />
          )}
        </TabsContent>
        <TabsContent value="models">
          {models.isPending ? (
            <LoadingState />
          ) : models.isError ? (
            <ErrorState error={models.error} onRetry={() => void models.refetch()} />
          ) : (
            <CompleteDataTable
              columns={modelColumns}
              data={models.data}
              getSearchText={(model) => `${model.name} ${model.manufacturer_name ?? ""}`}
              searchDescription={t("tablePilot.searchFields", {
                fields: `${t("app.name")}, ${t("assets.manufacturer")}`,
              })}
              sortFields={[
                { value: "name", label: t("app.name"), valueOf: (model) => model.name },
                {
                  value: "manufacturer",
                  label: t("assets.manufacturer"),
                  valueOf: (model) => model.manufacturer_name ?? null,
                },
              ]}
              minWidth={650}
              getRowId={(x) => x.id}
              total={models.data.length}
            />
          )}
        </TabsContent>
        <TabsContent value="meters">
          {meterTypes.isPending ? (
            <LoadingState />
          ) : meterTypes.isError ? (
            <ErrorState error={meterTypes.error} onRetry={() => void meterTypes.refetch()} />
          ) : (
            <CompleteDataTable
              columns={meterColumns}
              data={meterTypes.data ?? []}
              getSearchText={(mt) => `${mt.key} ${mt.name} ${mt.unit}`}
              searchDescription={t("tablePilot.searchFields", {
                fields: `${t("app.key")}, ${t("app.name")}`,
              })}
              sortFields={[{ value: "name", label: t("app.name"), valueOf: (mt) => mt.name }]}
              minWidth={600}
              getRowId={(x) => x.id}
              total={(meterTypes.data ?? []).length}
            />
          )}
        </TabsContent>
        <TabsContent value="categories">
          {categories.isPending ? (
            <LoadingState />
          ) : categories.isError ? (
            <ErrorState error={categories.error} onRetry={() => void categories.refetch()} />
          ) : (
            <CompleteDataTable
              columns={categoryColumns}
              data={categories.data ?? []}
              getSearchText={(cat) => `${cat.key} ${cat.name}`}
              searchDescription={t("tablePilot.searchFields", {
                fields: `${t("app.key")}, ${t("app.name")}`,
              })}
              sortFields={[{ value: "name", label: t("app.name"), valueOf: (cat) => cat.name }]}
              minWidth={600}
              getRowId={(x) => x.id}
              total={(categories.data ?? []).length}
            />
          )}
        </TabsContent>
        <TabsContent value="reorder">
          {policies.isPending ? (
            <LoadingState />
          ) : policies.isError ? (
            <ErrorState error={policies.error} onRetry={() => void policies.refetch()} />
          ) : (
            <CompleteDataTable
              columns={policyColumns}
              data={policies.data ?? []}
              getSearchText={(p) =>
                `${p.item_sku} ${p.item_name} ${p.location_code} ${p.location_path}`
              }
              searchDescription={t("tablePilot.searchFields", {
                fields: `${t("inventory.sku")}, ${t("app.name")}, ${t("inventory.location")}`,
              })}
              sortFields={[
                { value: "sku", label: t("inventory.sku"), valueOf: (p) => p.item_sku },
                {
                  value: "location",
                  label: t("inventory.location"),
                  valueOf: (p) => p.location_code,
                },
              ]}
              minWidth={700}
              getRowId={(x) => x.id}
              total={(policies.data ?? []).length}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
